/**
 * PartnerScoringRowControls — per-partner Download + Upload buttons.
 *
 * Lives inside each Teaming Partners row. One click downloads the
 * scoring XLSX tagged with that partner's name; one click uploads the
 * filled version back.
 *
 * v3.17.0 — Requirement #10.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Download, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function streamDownload(res, fallbackName) {
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') || '';
  const m = dispo.match(/filename="?([^";]+)"?/i);
  const filename = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PartnerScoringRowControls({
  pursuitId,
  partnerName,
  index,
  disabled = false,
}) {
  const { getAuthHeaders } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const sourceName = (partnerName || '').trim();

  const handleDownload = async () => {
    if (!sourceName) {
      toast.info('Set the partner\'s Legal Name first.');
      return;
    }
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        source_name: sourceName,
        source_role: 'partner',
      });
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/template.xlsx?${params}`,
        { headers: getAuthHeaders() },
      );
      if (res.status === 412) {
        const body = await res.json();
        toast.error(body?.detail?.message || 'No requirements yet — run the AI Shredder first.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await streamDownload(res, `fedbd-self-scoring_${sourceName}.xlsx`);
      toast.success(`Template downloaded for ${sourceName}`);
    } catch (e) {
      console.error(e);
      toast.error('Could not download partner template.');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!sourceName) {
      toast.info('Set the partner\'s Legal Name first.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_name', sourceName);
      fd.append('source_role', 'partner');
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/upload`,
        { method: 'POST', headers: getAuthHeaders(), body: fd },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      const skipped = body.skipped_invalid
        ? ` (${body.skipped_invalid} invalid rows skipped)` : '';
      toast.success(
        `${body.source_name}: ${body.rows_recorded} scores recorded${skipped}. ` +
        `They lead on ${body.recompute.partner_wins} requirements after merge.`,
      );
      window.dispatchEvent(new CustomEvent('partner-scoring-changed', {
        detail: { pursuitId },
      }));
    } catch (e) {
      console.error(e);
      toast.error(`Upload failed: ${typeof e?.message === 'string' ? e.message : 'unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200"
      data-testid={`partner-scoring-row-${index}`}
    >
      <span className="text-[11px] text-slate-500 uppercase tracking-wider">
        Self-scoring
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDownload}
        disabled={disabled || downloading || !sourceName}
        className="h-7 text-xs text-purple-700 border-purple-200 hover:bg-purple-50"
        data-testid={`partner-scoring-download-${index}`}
      >
        {downloading ? (
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        ) : (
          <Download className="w-3 h-3 mr-1" />
        )}
        Download Template
      </Button>
      <label
        className={`inline-flex items-center gap-1 h-7 px-2.5 text-xs border rounded-md cursor-pointer transition ${
          disabled || uploading || !sourceName
            ? 'text-slate-400 border-slate-200 cursor-not-allowed'
            : 'text-purple-700 border-purple-200 hover:bg-purple-50'
        }`}
        data-testid={`partner-scoring-upload-${index}`}
      >
        {uploading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Upload className="w-3 h-3" />
        )}
        Upload Response
        <input
          type="file"
          accept=".xlsx"
          className="hidden"
          disabled={disabled || uploading || !sourceName}
          onChange={(e) => {
            const f = e.target.files?.[0];
            handleUpload(f);
            e.target.value = '';
          }}
        />
      </label>
    </div>
  );
}
